import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { ArrowLeft, Check, CheckCheck, Loader2, MessageSquare, Plus, Send, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useSearchParams } from 'react-router-dom';
import { commsAPI, errMsg, fmtFull, fmtWhen, notifyCommsChanged } from '../../lib/communicationsAPI';
import { usePolling } from '../../hooks/useComms';
import { RichTextEditor } from './RichTextEditor';
import { RichBody } from './RichBody';
import { AttachmentList, AttachmentPicker } from './Attachments';

const Receipt = ({ m }) => (m.mine && m.direction === 'inbound' ? (
  <span className="inline-flex items-center gap-1" data-testid={`client-msg-receipt-${m.id}`}>
    {m.read_by_firm ? <><CheckCheck className="w-3 h-3" />Read by firm</> : <><Check className="w-3 h-3" />Received</>}
  </span>
) : null);

const ThreadView = ({ id, onBack }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [text, setText] = useState('');
  const [atts, setAtts] = useState([]);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const editorRef = useRef(null);
  const load = useCallback(() => commsAPI.get(id).then((r) => { setData(r.data); setError(null); }).catch((e) => setError(errMsg(e, 'Failed to load'))), [id]);
  usePolling(load, 12000, [id]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [data?.messages.length]);

  const send = async () => {
    if (!text.trim() && !atts.length) return;
    setSending(true);
    try {
      await commsAPI.reply(id, text, { body_format: 'html', attachment_ids: atts.map((a) => a.id) });
      editorRef.current?.clear(); setText(''); setAtts([]); toast.success('Message sent'); load(); notifyCommsChanged(); } catch (e) { toast.error(errMsg(e, 'Failed to send')); } finally { setSending(false); }
  };

  if (error) return <p className="text-sm text-destructive flex items-center gap-2" data-testid="client-thread-error"><AlertCircle className="w-4 h-4" />{error}</p>;
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />;
  return (
    <div className="space-y-3" data-testid="client-thread">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="client-thread-back"><ArrowLeft className="w-4 h-4 mr-1" />All messages</Button>
        <h3 className="font-semibold text-foreground truncate" data-testid="client-thread-subject">{data.conversation.subject}</h3>
      </div>
      <div className="space-y-3 max-h-[55vh] overflow-y-auto rounded-lg border border-border p-3 bg-muted/20">
        {data.messages.map((m) => (
          <div key={m.id} className={cn('flex', m.mine ? 'justify-end' : 'justify-start')} data-testid={`client-msg-${m.id}`}>
            <div className={cn('max-w-[85%] sm:max-w-[75%] rounded-2xl px-4 py-2.5', m.mine ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-background border border-border rounded-bl-md')}>
              {!m.mine && <p className="text-[11px] font-semibold text-foreground/70 mb-0.5">{m.sender_name}</p>}
              <RichBody body={m.body} format={m.body_format} className={m.mine ? 'comms-prose-invert' : ''} testId={`client-msg-body-${m.id}`} />
              <AttachmentList items={m.attachments} inverted={m.mine} />
              <p className={cn('mt-1 text-[10px] flex items-center gap-2', m.mine ? 'text-primary-foreground/75 justify-end' : 'text-muted-foreground')}>
                {fmtFull(m.sent_at)} <Receipt m={m} />
              </p>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="space-y-2">
        <RichTextEditor ref={editorRef} onChange={setText} onSubmit={send} placeholder="Write a reply…" testId="client-reply-input" />
        <AttachmentPicker value={atts} onChange={setAtts} conversationId={id} />
        <div className="flex justify-end">
          <Button onClick={send} disabled={sending || (!text.trim() && !atts.length)} data-testid="client-reply-send">
            {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}Send
          </Button>
        </div>
      </div>
    </div>
  );
};

const TopicPicker = ({ topic, setTopic }) => {
  const [t, setT] = useState(null);
  useEffect(() => { api.get('/api/portal/messages/topics').then((r) => setT(r.data)).catch(() => {}); }, []);
  if (!t) return null;
  const items = t.items[topic.kind] || [];
  return (
    <div className="flex flex-wrap gap-2">
      <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={topic.kind} onChange={(e) => setTopic({ kind: e.target.value, ref_id: '' })} data-testid="client-new-topic">
        {Object.entries(t.kinds).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      {topic.kind !== 'general' && (
        <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={topic.ref_id} onChange={(e) => setTopic({ ...topic, ref_id: e.target.value })} data-testid="client-new-topic-item">
          <option value="">{items.length ? 'Choose…' : 'Nothing to link'}</option>
          {items.map((i) => <option key={i.ref_id} value={i.ref_id}>{i.label}</option>)}
        </select>
      )}
    </div>
  );
};

const NewMessage = ({ onCreated, onCancel, initialTopic }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [atts, setAtts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [topic, setTopic] = useState(initialTopic || { kind: 'general', ref_id: '' });
  const submit = async () => {
    if (!subject.trim() || (!body.trim() && !atts.length)) { toast.error('Please add a subject and a message'); return; }
    setBusy(true);
    try {
      const r = await commsAPI.create({ subject, body, body_format: 'html', attachment_ids: atts.map((a) => a.id), topic: { kind: topic.kind, ref_id: topic.ref_id || null } });
      toast.success('Message sent to your accountant'); onCreated(r.data.conversation.id);
    } catch (e) { toast.error(errMsg(e, 'Failed to send')); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-lg border border-border p-4" data-testid="client-new-message-form">
      <TopicPicker topic={topic} setTopic={setTopic} />
      <Input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} data-testid="client-new-subject" />
      <RichTextEditor onChange={setBody} onSubmit={submit} placeholder="How can we help?" testId="client-new-body" />
      <AttachmentPicker value={atts} onChange={setAtts} />
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} data-testid="client-new-cancel">Cancel</Button>
        <Button onClick={submit} disabled={busy} data-testid="client-new-send">{busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Send message</Button>
      </div>
    </div>
  );
};

// Client portal "Messages": start, view and reply to conversations with the firm (polling).
export const ClientPortalMessages = () => {
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [params] = useSearchParams();
  const [openId, setOpenId] = useState(params.get('c'));
  useEffect(() => { if (params.get('c')) setOpenId(params.get('c')); }, [params]);
  const [composing, setComposing] = useState(!!params.get('topic'));
  const initialTopic = params.get('topic') ? { kind: params.get('topic'), ref_id: params.get('ref') || '' } : null;
  const [filters, setFilters] = useState({ client_id: '', topic: '', q: '' });
  const [ents, setEnts] = useState([]);
  useEffect(() => { api.get('/api/portal-access/my').then((r) => setEnts(r.data.entities)).catch(() => {}); }, []);
  const load = useCallback(() => commsAPI.list({ limit: 100, client_id: filters.client_id || undefined, topic: filters.topic || undefined })
    .then((r) => { setList(r.data.conversations); setError(null); }).catch((e) => setError(errMsg(e, 'Failed to load messages'))), [filters.client_id, filters.topic]);
  const shown = (list || []).filter((c) => !filters.q || `${c.subject} ${c.last_message_preview}`.toLowerCase().includes(filters.q.toLowerCase()));
  usePolling(load, 12000, [], !openId);

  return (
    <Card data-testid="client-portal-messages">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-primary" />
          <CardTitle className="text-lg">Messages</CardTitle>
        </div>
        {!openId && !composing && <Button size="sm" onClick={() => setComposing(true)} data-testid="client-new-message-btn"><Plus className="w-4 h-4 mr-1.5" />New message</Button>}
      </CardHeader>
      <CardContent className="space-y-3">
        {openId ? <ThreadView id={openId} onBack={() => { setOpenId(null); load(); }} /> : (
          <>
            {composing && <NewMessage initialTopic={initialTopic} onCancel={() => setComposing(false)} onCreated={(id) => { setComposing(false); setOpenId(id); notifyCommsChanged(); }} />}
            <div className="flex flex-wrap gap-2" data-testid="client-messages-filters">
              <Input className="max-w-xs" placeholder="Search messages" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="client-messages-search" />
              {ents.length > 1 && (
                <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={filters.client_id} onChange={(e) => setFilters({ ...filters, client_id: e.target.value })} data-testid="client-messages-entity-filter">
                  <option value="">All my entities</option>{ents.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              )}
              <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={filters.topic} onChange={(e) => setFilters({ ...filters, topic: e.target.value })} data-testid="client-messages-topic-filter">
                <option value="">All topics</option>
                {[['general', 'General enquiry'], ['tax_return', 'Tax return'], ['bas', 'BAS / IAS'], ['document_request', 'Document request'], ['nomination', 'ATO nomination'], ['engagement_letter', 'Engagement letter']].map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            {error ? <p className="text-sm text-destructive" data-testid="client-messages-error">{error}</p>
              : list === null ? <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                : list.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8 text-sm" data-testid="client-messages-empty">No messages yet. Start a conversation with your accountant any time.</p>
                ) : (
                  <ul className="divide-y divide-border rounded-lg border border-border" data-testid="client-conversation-list">
                    {shown.map((c) => (
                      <li key={c.id}>
                        <button type="button" onClick={() => setOpenId(c.id)} className="w-full text-left px-4 py-3 hover:bg-muted/50 flex gap-3 items-start" data-testid={`client-conversation-${c.id}`}>
                          {c.unread ? <span className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" aria-label="New reply" /> : <span className="w-2 shrink-0" />}
                          <span className="min-w-0 flex-1">
                            <span className={cn('block truncate text-sm', c.unread ? 'font-semibold' : 'font-medium')}>{c.subject}</span>
                            <span className="block truncate text-xs text-muted-foreground">{c.last_message_preview}</span>
                            <span className="flex gap-1.5 mt-1">
                              {c.entity_name && <span className="rounded bg-primary/10 text-primary px-1.5 py-0.5 text-[11px]" data-testid={`client-conversation-entity-${c.id}`}>{c.entity_name}</span>}
                              {c.topic && c.topic.kind !== 'general' && <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground" data-testid={`client-conversation-topic-${c.id}`}>{c.topic.label}</span>}
                            </span>
                          </span>
                          <span className="text-[11px] text-muted-foreground shrink-0">{c.closed ? 'Closed · ' : ''}{fmtWhen(c.last_message_at)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
